import { useState } from 'react';
import type { MutableRefObject } from 'react';
import { toast } from 'sonner';
import type { CreateUserRoleDto, UpdateUserRoleDto } from '@/services/userRoleService';
import { buildPermissionsPayload } from '../permissionsPayload';
import type { PermissionState } from './permissionState';
import { buildSnapshotFromState, computePermissionDiff, type PermissionDiff, type PermissionSnapshot } from './permissionDiffHelpers';
import type { UserRoleServiceInstance } from './useEditRoleData';

export interface UseRoleSaveOptions {
  roleId?: string;
  isCreateMode: boolean;
  userRoleService: UserRoleServiceInstance;
  permissions: PermissionState;
  isAdministrator: boolean;
  roleName: string;
  roleDescription: string;
  isActive: boolean;
  totalPermissionsCount: number;
  initialSnapshotRef: MutableRefObject<PermissionSnapshot | null>;
  setNameError: (message: string) => void;
  setActiveTab: (tab: string) => void;
  setHasUnsavedChanges: (value: boolean) => void;
  onSaved: () => void;
}

/** Owns role validation, the save confirmation diff and the create/update calls. */
export const useRoleSave = ({
  roleId,
  isCreateMode,
  userRoleService,
  permissions,
  isAdministrator,
  roleName,
  roleDescription,
  isActive,
  totalPermissionsCount,
  initialSnapshotRef,
  setNameError,
  setActiveTab,
  setHasUnsavedChanges,
  onSaved,
}: UseRoleSaveOptions) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState<PermissionDiff>({ added: [], removed: [] });

  const performSave = async () => {
    setIsSaving(true);
    try {
      const permissionsPayload = buildPermissionsPayload(permissions, isAdministrator);

      if (isCreateMode) {
        const createDto: CreateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.createUserRole(createDto);
      } else {
        const updateDto: UpdateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.updateUserRole(Number(roleId), updateDto);
      }

      window.dispatchEvent(new Event('rolesUpdated'));
      window.dispatchEvent(new Event('permissionsUpdated'));
      setIsSaving(false);
      setHasUnsavedChanges(false);
      setShowSaveConfirmation(false);

      toast.success(isCreateMode ? 'Role created successfully' : 'Role updated successfully', {
        description: `${roleName} has been ${isCreateMode ? 'created' : 'updated'} with ${totalPermissionsCount} permissions.`,
      });

      onSaved();
    } catch (error: unknown) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Detect duplicate role name errors from both the backend validation
      // and the SQL UNIQUE KEY constraint (in case the pre-check is bypassed)
      if (
        errorMessage.includes('already exists') ||
        errorMessage.includes('UQ_UserRoles') ||
        errorMessage.includes('UNIQUE KEY') ||
        errorMessage.includes('duplicate key')
      ) {
        setNameError(`A role with the name '${roleName.trim()}' already exists`);
        setActiveTab('details');
        toast.error('Duplicate role name', { description: `A role with the name '${roleName.trim()}' already exists. Please choose a different name.` });
      } else {
        toast.error('Save failed', { description: errorMessage || 'Failed to save role' });
      }
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!roleName.trim()) { setNameError('Role name is required'); setActiveTab('details'); return; }
    if (roleName.trim().length < 2) { setNameError('Role name must be at least 2 characters'); setActiveTab('details'); return; }
    setNameError('');
    if (!isCreateMode) {
      setPermissionChanges(computePermissionDiff(buildSnapshotFromState(permissions), initialSnapshotRef.current));
      setShowSaveConfirmation(true);
    } else {
      await performSave();
    }
  };

  return { isSaving, showSaveConfirmation, setShowSaveConfirmation, permissionChanges, performSave, handleSave };
};
